import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

// P010 — NEW component. Wraps a raw `<input type="checkbox" className="toggle">`
// (DaisyUI toggle) with variant/size mapping + the designer's form-row label
// spec (switch-right, matches the "Toggle with Label (Form Row)" precedent on
// DesignSystemPage). `size` is string-union (not the native numeric attribute)
// like Input/Badge, so it's Omit'd from the extended HTML attributes.
export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: ReactNode
  variant?: 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'error' | 'info'
  size?: 'sm' | 'md'
}

const VARIANT_CLASSES: Record<NonNullable<ToggleProps['variant']>, string> = {
  primary: 'toggle-primary',
  secondary: 'toggle-secondary',
  accent: 'toggle-accent',
  success: 'toggle-success',
  warning: 'toggle-warning',
  error: 'toggle-error',
  info: 'toggle-info',
}

const SIZE_CLASSES: Record<NonNullable<ToggleProps['size']>, string> = {
  sm: 'toggle-sm',
  md: '',
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  ({ className = '', label, variant, size = 'md', disabled, ...props }, ref) => {
    const input = (
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className={`toggle ${variant ? VARIANT_CLASSES[variant] : ''} ${SIZE_CLASSES[size]} ${className}`}
        {...props}
      />
    )

    if (!label) return input

    return (
      <label className={`flex items-center justify-between gap-3 cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
        <span className="text-sm">{label}</span>
        {input}
      </label>
    )
  },
)

Toggle.displayName = 'Toggle'
